<script setup>
import { computed } from 'vue';
import { Head, usePage, Link } from '@inertiajs/vue3';
import DagensFund from '@/Components/DagensFund.vue';
import PriceCompareLayout from '@/Layouts/PriceCompareLayout.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import { sanitizeWithBreaks } from '@/Utils/sanitize';
import Blokliste from '@/Components/Blokke/Blokliste.vue';

const page = usePage();
const seo = page.props.seo;
const article = page.props.article;

const breadcrumbs = page.props.breadcrumbs;

// Relaterede artikler. Tom liste er et gyldigt svar, ikke en fejl.
const lignende = computed(() => page.props.lignende ?? []);

// Indholdsblokke under broedteksten. De fleste artikler har ingen.
const blokke = computed(() => page.props.blokke ?? []);

function formatDanishDateTime(dateString) {
    const date = new Date(dateString);
    const months = [
        'januar', 'februar', 'marts', 'april', 'maj', 'juni',
        'juli', 'august', 'september', 'oktober', 'november', 'december'
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}. ${month} ${year} kl. ${hours}.${minutes}`;
}
</script>

<template>
    <PriceCompareLayout>
        <div class="container-fluid">
            <div class="row">
                <div class="col-12">
                    <BreadcrumbBar :breadcrumbs="breadcrumbs" class="mb-4" />
                </div>
            </div>
            <div class="row justify-content-center pt-1 px-lg-2">
                <div class="col-lg-8 col-xl-9">
                    <div class="card border-0 shadow-sm mb-4 bg-white">
                        <div class="card-body p-4 p-md-5">
                            <h1 class="display-5 mb-2">{{ article.name }}</h1>
                            <div class="d-flex align-items-center mb-3">
                                <span class="badge bg-info me-2">{{ article.category || 'Artikel' }}</span>
                                <small class="text-muted">{{ formatDanishDateTime(article.post_date) }}</small>
                                <span v-if="article.author" class="ms-3"><i class="bi bi-person"></i> {{ article.author }}</span>
                            </div>
                            <div v-if="article.cover_image" class="mb-4 text-center">
                                <img :src="route('getArticleImage', article.cover_image)" :alt="article.name" class="img-fluid rounded shadow-sm" style="max-height: 400px; object-fit: cover;" />
                                <!--
                                    Pressebilleder stilles til rådighed til redaktionel brug MED
                                    kreditering. Uden den linje opfylder vi ikke betingelsen.
                                -->
                                <div v-if="article.cover_image_credit" class="text-muted small mt-2">
                                    {{ article.cover_image_credit }}
                                </div>
                            </div>
                            <div class="mb-4">
                                <p class="lead">{{ article.teaser_text }}</p>
                            </div>
                            <div v-html="sanitizeWithBreaks(article.full_text)" class="article-content mb-5"></div>

                            <!-- Blokke under broedteksten. Tom paa de fleste artikler. -->
                            <Blokliste v-if="blokke.length" :blokke="blokke" class="mb-5" />
                            <div class="mb-4">
                                <div class="row align-items-center">
                                    <div class="col-12 col-md-6 mb-2 mb-md-0">
                                        <!--
                                            Kilden skal NAVNGIVES, ikke bare linkes.
                                            Ophavsretslovens § 3 giver ophavsmanden krav på
                                            navngivelse i overensstemmelse med god skik, og § 22
                                            tillader citat på betingelse af at kilden angives. En
                                            knap der hedder "Læs original artikel" nævner ingen.
                                        -->
                                        <div v-if="article.external_source" class="text-muted small mb-2">
                                            Kilde:
                                            <a v-if="article.external_link" :href="article.external_link" target="_blank" rel="noopener nofollow">
                                                {{ article.external_source }}
                                            </a>
                                            <span v-else>{{ article.external_source }}</span>
                                            <span v-if="article.external_author"> ved {{ article.external_author }}</span>
                                        </div>
                                        <div v-if="article.external_link">
                                            <a :href="article.external_link" target="_blank" rel="noopener" class="btn btn-outline-secondary w-100">
                                                Læs original artikel <i class="bi bi-box-arrow-up-right ms-1"></i>
                                            </a>
                                        </div>
                                    </div>
                                    <div class="col-12 col-md-6 text-md-end" v-if="article.is_ai_generated">
                                        <span class="badge bg-warning text-dark small">AI-genereret indhold</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Sidebar column -->
                <div class="col-lg-4 col-xl-3">
                    <!-- Var en tom kasse med "300 x 250". -->
                    <div class="card border-0 bg-light mb-4" v-if="$page.props.dagensFund?.length">
                        <div class="card-body p-3">
                            <DagensFund :fund="$page.props.dagensFund" :interval="$page.props.fundRotationMs || 6000" />
                        </div>
                    </div>
                    <div v-if="lignende.length" class="card border-0 shadow-sm">
                        <div class="card-body p-4">
                            <h4 class="card-title mb-3">Relaterede artikler</h4>
                            <ul class="list-unstyled mb-0">
                                <li v-for="a in lignende" :key="a.id" class="mb-3 pb-3 border-bottom">
                                    <Link :href="`/artikler/${a.slug}`" class="text-decoration-none fw-semibold d-block">
                                        {{ a.navn }}
                                    </Link>
                                    <div v-if="a.teaser" class="text-muted small mt-1">
                                        {{ a.teaser.length > 110 ? a.teaser.slice(0, 110) + '...' : a.teaser }}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PriceCompareLayout>
</template>

<style scoped>
.article-content img {
    max-width: 100%;
    height: auto;
    margin: 1rem 0;
}
.article-content h2, .article-content h3 {
    margin-top: 2rem;
    margin-bottom: 1rem;
}
.card {
    border-radius: 8px;
}
</style>
